import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { MOTION_GLOBAL_CSS } from './motion/keyframes.js';

const preconnect = document.createElement('link');
preconnect.rel = 'preconnect';
preconnect.href = 'https://fonts.gstatic.com';
preconnect.crossOrigin = 'anonymous';
document.head.appendChild(preconnect);

const fontsLink = document.createElement('link');
fontsLink.rel = 'stylesheet';
fontsLink.href =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
document.head.appendChild(fontsLink);

// Global styles for the iframe viewport + shared keyframes.
const GLOBAL_CSS = `
  /* Iframe viewport reset — prevents body-margin clipping and square halo behind rounded shells. */
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
    /* Intentionally no color-scheme — Chrome paints a dark canvas behind the
       transparent body when the system is in dark mode, and that dark layer
       leaks through and hides the host page through the (otherwise transparent)
       iframe. Leaving color-scheme unset keeps the user-agent canvas truly
       transparent so the host page shows through. */
    box-sizing: border-box !important;
    overflow: hidden !important;
    width: 100% !important;
    min-height: 100% !important;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    /* Disable iOS auto-zoom on input focus when font-size >= 16px is enforced. */
    -webkit-text-size-adjust: 100%;
  }
  /* Inputs default to 16px so iOS Safari doesn't auto-zoom on focus. */
  input, textarea, select { font-size: 16px; }
  @media (min-width: 480px) {
    input, textarea, select { font-size: 14px; }
  }
  /* Date / time inputs render two icons by default — the browser's native
     calendar-picker-indicator on the right edge, plus our themed trailing
     icon. Stretch the native indicator across the full input and make it
     transparent: it stays clickable (clicking anywhere on the field opens
     the picker) but visually disappears, leaving only our themed icon. */
  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="datetime-local"]::-webkit-calendar-picker-indicator,
  input[type="time"]::-webkit-calendar-picker-indicator,
  input[type="month"]::-webkit-calendar-picker-indicator,
  input[type="week"]::-webkit-calendar-picker-indicator {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    opacity: 0;
    background: transparent;
    color: transparent;
  }
  /* Date inputs default to inline-block which won't accept the absolute
     ::-webkit-calendar-picker-indicator. Make the input itself the
     positioning context so the indicator can stretch to fill it. */
  input[type="date"],
  input[type="datetime-local"],
  input[type="time"],
  input[type="month"],
  input[type="week"] {
    position: relative;
  }
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root, * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  @keyframes chirp-skeleton {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  @keyframes chirp-spin {
    to { transform: rotate(360deg); }
  }
${MOTION_GLOBAL_CSS}
`;
// HMR-safe: drop any stale chirp style tag before re-appending so rules don't stack.
document.head.querySelectorAll('style[data-chirp="global"]').forEach((node) => node.remove());
const globalStyle = document.createElement('style');
globalStyle.setAttribute('data-chirp', 'global');
globalStyle.textContent = GLOBAL_CSS;
document.head.appendChild(globalStyle);

const container = document.getElementById('root');
if (container) {
  createRoot(container).render(<App />);
} else {
  console.error('[chirp-shell] no #root element found');
}

// Prefetch the Announcement + Feedback renderer chunks during browser idle
// so their dynamic imports later land instantly. Survey is already in the
// base bundle (see vite.config.ts) and doesn't need this. We use the actual
// dynamic import call (not <link rel="modulepreload">) so Vite's hashed
// chunk filenames work without manifest plumbing — the imports only have
// side effects we don't act on here.
const prefetch = (cb: () => void): void => {
  const win = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 800);
  }
};
prefetch(() => {
  void import('./renderers/Announcement/index.js').catch(() => {});
  void import('./renderers/FeedbackWidget/index.js').catch(() => {});
});
